import { useEffect, useState } from "react";
import type { AuditEvent } from "@shared/core/types";
import { api } from "../lib/api";
import { useToastStore } from "../stores/toast";
import { PageHeader } from "../components/ui/page-header";
import { Button } from "../components/ui/button";
import { Field, Input } from "../components/ui/input";
import { formatDateTime } from "../lib/format";

/** Journal de consultation et de modification (direction, administrateurs). */
export function AuditPage() {
  const push = useToastStore((s) => s.push);
  const [rows, setRows] = useState<AuditEvent[]>([]);
  const [action, setAction] = useState("");
  const [targetId, setTargetId] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = new URLSearchParams();
      if (action.trim()) params.set("action", action.trim());
      if (targetId.trim()) params.set("targetId", targetId.trim());
      params.set("limit", "200");
      setRows(await api<AuditEvent[]>(`/api/audit?${params.toString()}`));
    } catch {
      push("danger", "Impossible de charger le journal");
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="page">
      <PageHeader
        title="Journal"
        subtitle="Qui a consulté ou modifié quoi, et quand. Rien ne s'y efface."
        actions={
          <Button variant="ghost" onClick={load} disabled={busy}>
            Actualiser
          </Button>
        }
      />
      <form
        className="form-grid"
        onSubmit={(e) => {
          e.preventDefault();
          void load();
        }}
      >
        <Field label="Action" hint="ex. candidats.dossier.consulte">
          <Input value={action} onChange={(e) => setAction(e.target.value)} />
        </Field>
        <Field label="Identifiant de la cible">
          <Input value={targetId} onChange={(e) => setTargetId(e.target.value)} />
        </Field>
        <div className="form-grid__full">
          <Button type="submit" variant="primary" disabled={busy}>
            Filtrer
          </Button>
        </div>
      </form>
      <table className="table">
        <thead>
          <tr>
            <th>Quand</th>
            <th>Qui</th>
            <th>Action</th>
            <th className="table__col--wide">Cible</th>
            <th className="table__col--wide">Adresse</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id}>
              <td>{formatDateTime(r.at, { dateStyle: "short", timeStyle: "medium" })}</td>
              <td>
                {r.userEmail ?? "—"}
                {r.role ? <span className="muted"> · {r.role}</span> : null}
              </td>
              <td>
                <code>{r.action}</code>
              </td>
              <td className="table__col--wide">
                {r.targetType ? (
                  <>
                    {r.targetType} <code>{r.targetId}</code>
                  </>
                ) : (
                  "—"
                )}
              </td>
              <td className="table__col--wide">{r.ip ?? "—"}</td>
            </tr>
          ))}
          {rows.length === 0 ? (
            <tr>
              <td colSpan={5} className="table__empty">
                Aucun événement.
              </td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}
